import React, { memo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Tooltip } from 'antd';
import { ApartmentOutlined } from '@ant-design/icons';

const SwitchNode = ({ data, isConnectable, selected }) => {
    // Track which handle is being hovered
    const [hoveredHandle, setHoveredHandle] = useState(null);

    // Get switch information
    const getSwitchInfo = () => {
        const type = data.switchType || data.config?.type;
        const expression = data.config?.expression || '';
        const field = data.config?.field || '';
        const prompt = data.config?.prompt || '';

        // Ensure outputs array is available
        const outputs = data.outputs || data.config?.outputs || ['Option 1', 'Option 2', 'Default'];

        let displayText = '';
        let description = '';

        switch (type) {
            case 'javascript':
                displayText = expression.length > 40 ? expression.substring(0, 37) + '...' : expression;
                description = `Switch based on: ${expression}`;
                break;
            case 'field':
                displayText = `${field}`;
                description = `Switch on field value: ${field}`;
                break;
            case 'ai':
                displayText = prompt.length > 40 ? prompt.substring(0, 37) + '...' : prompt;
                description = `AI Evaluation: ${prompt}`;
                break;
            default:
                displayText = 'Switch not set';
                description = 'Switch not configured';
        }

        return { type, displayText, description, outputs };
    };

    const switchInfo = getSwitchInfo();

    // Show execution count badge if available
    const ExecutionCountBadge = () => {
        if (data.executionCount === undefined) return null;

        return (
            <div style={{
                position: 'absolute',
                top: -8,
                right: -8,
                background: '#1890ff',
                color: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 10
            }}>
                {data.executionCount}
            </div>
        );
    };

    // Calculate node height based on number of outputs
    const nodeHeight = Math.max(120, 80 + switchInfo.outputs.length * 25);

    // Base color for handles and indicators
    const baseColor = '#722ed1'; // Purple

    // Get handle positions for each output
    const getHandlePosition = (index, total) => {
        if (index < 3) {
            // Bottom positions for first 3 options
            const leftPosition = index === 0 ? 25 : (index === 1 ? 50 : 75);
            return {
                position: Position.Bottom,
                style: {
                    left: `${leftPosition}%`,
                    bottom: -6
                }
            };
        } else {
            // Right side positions for remaining options
            return {
                position: Position.Right,
                style: {
                    top: `${30 + ((index - 3) * 15)}%`,
                    right: -6
                }
            };
        }
    };

    // Enhanced handle style with stable hover effect
    const getHandleStyle = (id, baseStyle) => {
        const isHovering = hoveredHandle === id;

        // We'll use a consistent size and show the hover effect with just the glow
        return {
            background: baseColor,
            width: 12,
            height: 12,
            border: '2px solid white',
            boxShadow: isHovering ? `0 0 0 6px ${baseColor}30` : 'none',
            zIndex: 10,
            transition: 'box-shadow 0.2s ease',
            ...baseStyle
        };
    };

    return (
        <Tooltip title={switchInfo.description}>
            <div
                style={{
                    width: 220,
                    minHeight: nodeHeight,
                    position: 'relative',
                    background: selected ? '#f9f0ff' : 'white',
                    border: selected ? '1px solid #722ed1' : '1px solid #d9d9d9',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    fontFamily: 'Arial, sans-serif'
                }}
            >
                <ExecutionCountBadge />

                {/* Header */}
                <div style={{
                    background: baseColor,
                    color: 'white',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #d9d9d9'
                }}>
                    <ApartmentOutlined style={{ fontSize: 16, marginRight: 8 }} />
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {data.label || 'Switch'}
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '10px 16px' }}>
                    <div style={{
                        fontSize: '13px',
                        fontFamily: switchInfo.type === 'javascript' ? 'monospace' : 'inherit',
                        marginBottom: '8px'
                    }}>
                        {switchInfo.displayText}
                    </div>
                </div>

                {/* Output Options */}
                <div style={{
                    borderTop: '1px solid #e8e8e8',
                    padding: '8px 16px'
                }}>
                    {switchInfo.outputs.map((output, index) => {
                        return (
                            <div key={index} style={{
                                background: 'rgba(114, 46, 209, 0.05)',
                                padding: '6px 10px',
                                margin: '5px 0',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: baseColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                position: 'relative',
                                borderLeft: `3px solid ${baseColor}`,
                                paddingLeft: '12px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginRight: '5px'
                                }}>
                                    <span style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        background: 'rgba(114, 46, 209, 0.1)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '8px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: baseColor,
                                        border: `1px solid ${baseColor}`
                                    }}>
                                        {index + 1}
                                    </span>
                                    {output === 'Default' ? (
                                        <strong>Default/Other</strong>
                                    ) : output}
                                </div>
                                <span style={{
                                    position: 'absolute',
                                    right: '8px',
                                    color: baseColor,
                                    fontWeight: 'bold'
                                }}>
                                    {index < 3 ? '↓' : '→'}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Input handle */}
                <Handle
                    type="target"
                    position={Position.Top}
                    id="in"
                    style={getHandleStyle("in", { top: -6 })}
                    isConnectable={isConnectable}
                    onMouseEnter={() => setHoveredHandle("in")}
                    onMouseLeave={() => setHoveredHandle(null)}
                />

                {/* Dynamically create output handles for each option */}
                {switchInfo.outputs.map((output, index) => {
                    const { position, style } = getHandlePosition(
                        index,
                        switchInfo.outputs.length
                    );

                    const handleId = `switch-${output}-${index}`; // Add index to ensure uniqueness

                    return (
                        <Handle
                            key={index}
                            type="source"
                            position={position}
                            id={`switch-${output}`}
                            style={getHandleStyle(handleId, style)}
                            isConnectable={isConnectable}
                            onMouseEnter={() => setHoveredHandle(handleId)}
                            onMouseLeave={() => setHoveredHandle(null)}
                        />
                    );
                })}

                {/* Add numbered indicators near the handles for better visual connection */}
                {switchInfo.outputs.slice(0, 3).map((output, index) => {
                    const leftPosition = index === 0 ? '25%' : (index === 1 ? '50%' : '75%');

                    return (
                        <div
                            key={`indicator-${index}`}
                            style={{
                                position: 'absolute',
                                bottom: '3px',
                                left: leftPosition,
                                transform: 'translateX(-50%)',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: 'white',
                                border: `1px solid ${baseColor}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                color: baseColor,
                                zIndex: 5
                            }}
                        >
                            {index + 1}
                        </div>
                    );
                })}

                {/* Add numbered indicators for side handles */}
                {switchInfo.outputs.slice(3).map((output, index) => {
                    return (
                        <div
                            key={`indicator-side-${index}`}
                            style={{
                                position: 'absolute',
                                top: `${30 + ((index) * 15)}%`,
                                right: '3px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: 'white',
                                border: `1px solid ${baseColor}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                color: baseColor,
                                zIndex: 5
                            }}
                        >
                            {index + 4}
                        </div>
                    );
                })}
            </div>
        </Tooltip>
    );
};

export default memo(SwitchNode);
